// src/pages/violations/ViolationHome.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import AppDataGrid from "../../components/AppDataGrid";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import { MasterDetailModule } from "ag-grid-enterprise";
import { FaPlus, FaMinus } from "react-icons/fa";
import { BASE_URL } from "../../utils";

ModuleRegistry.registerModules([AllCommunityModule, MasterDetailModule]);

function pad2(n) {
  return String(n).padStart(2, "0");
}

function formatDateMDY(dateLike) {
  if (!dateLike && dateLike !== 0) return "";
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return String(dateLike);
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

function formatMoney(v) {
  if (v == null || v === "") return "";
  const num = Number(v);
  if (!Number.isFinite(num)) return String(v);
  return num.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

const BORO_OPTIONS = [
  { label: "", value: "" },
  { label: "1 - Manhattan", value: "1" },
  { label: "2 - Bronx", value: "2" },
  { label: "3 - Brooklyn", value: "3" },
  { label: "4 - Queens", value: "4" },
  { label: "5 - Staten Island", value: "5" },
  { label: "MN - Manhattan", value: "MN" },
  { label: "BX - Bronx", value: "BX" },
  { label: "BK - Brooklyn", value: "BK" },
  { label: "QN - Queens", value: "QN" },
  { label: "SI - Staten Island", value: "SI" },
];

export default function ViolationHome() {
  const gridRef = useRef(null);
  const gridApiRef = useRef(null);
  const columnApiRef = useRef(null);
  const isFetchingRef = useRef(false);
  const lastFetchKeyRef = useRef("");

  const [meta, setMeta] = useState({ sources: [], boros: [], statuses: [] });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showColumns, setShowColumns] = useState(false);
  const [availableColumns, setAvailableColumns] = useState([]);

  const [filters, setFilters] = useState({
    q: "",
    source: "",
    boro: "",
    block: "",
    lot: "",
    issueFrom: "",
    issueTo: "",
    status: "",
    hasBalanceDue: false,
    rentImpairing: false,
  });

  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [rowData, setRowData] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pageInput, setPageInput] = useState("");
  const [sortBy, setSortBy] = useState("updated_at");
  const [sortDir, setSortDir] = useState("DESC");
  const [tab, setTab] = useState("all");
  const [selectedBbls, setSelectedBbls] = useState(new Set());

  const [gridHeight, setGridHeight] = useState(Math.max(320, window.innerHeight - 240));
  const effectiveSortBy = tab === "hot" ? "violations_count" : sortBy;
  const effectiveSortDir = tab === "hot" ? "DESC" : sortDir;

  useEffect(() => {
    function onResize() {
      setGridHeight(Math.max(320, window.innerHeight - 240));
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/violations/meta`);
        if (!mounted) return;
        setMeta({
          sources: res.data?.sources || [],
          boros: res.data?.boros || [],
          statuses: res.data?.statuses || [],
        });
      } catch (err) {
        console.warn("violations meta fetch failed", err);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const fetchData = useCallback(
    async (p = page, s = pageSize, applied = appliedFilters) => {
      const fetchKey = JSON.stringify({
        p,
        s,
        sortBy: effectiveSortBy,
        sortDir: effectiveSortDir,
        tab,
        applied,
      });
      if (isFetchingRef.current && lastFetchKeyRef.current === fetchKey) return;
      isFetchingRef.current = true;
      lastFetchKeyRef.current = fetchKey;
      setLoading(true);
      try {
        gridApiRef.current?.showLoadingOverlay?.();
        const res = await axios.get(`${BASE_URL}/api/violations/bbl`, {
          params: {
            page: p,
            pageSize: s,
            sortBy: effectiveSortBy,
            sortDir: effectiveSortDir,
            isDeleted: tab === "deleted" ? 1 : 0,
            hot: tab === "hot" ? 1 : undefined,
            q: applied.q || undefined,
            source: applied.source || undefined,
            boro: applied.boro || undefined,
            block: applied.block || undefined,
            lot: applied.lot || undefined,
            issueFrom: applied.issueFrom || undefined,
            issueTo: applied.issueTo || undefined,
            status: applied.status || undefined,
            hasBalanceDue: applied.hasBalanceDue ? 1 : undefined,
            rentImpairing: applied.rentImpairing ? 1 : undefined,
          },
        });

        const rows = res.data?.data || [];
        setRowData(rows);
        setColumnDefs(buildDynamicColumns(rows));
        setTotal(res.data?.total || 0);
        const nextPage = Number(res.data?.page ?? p);
        const nextPageSize = Number(res.data?.pageSize ?? s);
        if (Number.isFinite(nextPage) && nextPage !== page) setPage(nextPage);
        if (Number.isFinite(nextPageSize) && nextPageSize !== pageSize) setPageSize(nextPageSize);
        setSelectedBbls(new Set());
        setTimeout(() => gridApiRef.current?.hideOverlay?.(), 80);
      } catch (err) {
        console.error("violations fetch error", err);
        setRowData([]);
        setTotal(0);
        try {
          gridApiRef.current?.showNoRowsOverlay?.();
        } catch (e) {}
      } finally {
        setLoading(false);
        isFetchingRef.current = false;
      }
    },
    [appliedFilters, page, pageSize, sortBy, sortDir, tab, effectiveSortBy, effectiveSortDir]
  );

  const clearSelection = useCallback(() => {
    setSelectedBbls(new Set());
    gridApiRef.current?.deselectAll?.();
  }, []);

  useEffect(() => {
    fetchData(page, pageSize, appliedFilters);
  }, [fetchData, page, pageSize, appliedFilters]);

  useEffect(() => {
    setPage(1);
    clearSelection();
  }, [tab, clearSelection]);

  useEffect(() => {
    if (tab !== "hot") return;
    try {
      gridApiRef.current?.setSortModel?.([{ colId: "violations_count", sort: "desc" }]);
    } catch (e) {}
  }, [tab]);

  const anySelected = selectedBbls.size > 0;

  useEffect(() => {
    const t = setTimeout(() => {
      setAppliedFilters((prev) => ({ ...prev, q: filters.q }));
      setPage(1);
    }, 450);
    return () => clearTimeout(t);
  }, [filters.q]);

  const applyFilters = () => {
    setPage(1);
    setAppliedFilters({ ...filters });
  };

  const clearFilters = () => {
    const cleared = {
      q: "",
      source: "",
      boro: "",
      block: "",
      lot: "",
      issueFrom: "",
      issueTo: "",
      status: "",
      hasBalanceDue: false,
      rentImpairing: false,
    };
    setFilters(cleared);
    setAppliedFilters(cleared);
    setPage(1);
  };

  const removeChip = (key) => {
    const cleared = { ...appliedFilters, [key]: Array.isArray(appliedFilters[key]) ? [] : "" };
    if (key === "hasBalanceDue" || key === "rentImpairing") cleared[key] = false;
    setAppliedFilters(cleared);
    setFilters((prev) => ({ ...prev, [key]: cleared[key] }));
    setPage(1);
  };

  const appliedChips = useMemo(() => {
    const chips = [];
    const f = appliedFilters || {};
    if (f.q) chips.push({ key: "q", label: `Search: ${f.q}` });
    if (f.source) chips.push({ key: "source", label: `Source: ${f.source}` });
    if (f.boro) chips.push({ key: "boro", label: `Boro: ${f.boro}` });
    if (f.block) chips.push({ key: "block", label: `Block: ${f.block}` });
    if (f.lot) chips.push({ key: "lot", label: `Lot: ${f.lot}` });
    if (f.issueFrom) chips.push({ key: "issueFrom", label: `Issue from: ${f.issueFrom}` });
    if (f.issueTo) chips.push({ key: "issueTo", label: `Issue to: ${f.issueTo}` });
    if (f.status) chips.push({ key: "status", label: `Status: ${f.status}` });
    if (f.hasBalanceDue) chips.push({ key: "hasBalanceDue", label: "Has balance due" });
    if (f.rentImpairing) chips.push({ key: "rentImpairing", label: "Rent impairing" });
    return chips;
  }, [appliedFilters]);

  const onExportCsv = async () => {
    try {
      const params = new URLSearchParams();
      params.set("sortBy", effectiveSortBy);
      params.set("sortDir", effectiveSortDir);
      params.set("isDeleted", tab === "deleted" ? "1" : "0");
      if (tab === "hot") params.set("hot", "1");
      if (appliedFilters.q) params.set("q", appliedFilters.q);
      if (appliedFilters.source) params.set("source", appliedFilters.source);
      if (appliedFilters.boro) params.set("boro", appliedFilters.boro);
      if (appliedFilters.block) params.set("block", appliedFilters.block);
      if (appliedFilters.lot) params.set("lot", appliedFilters.lot);
      if (appliedFilters.issueFrom) params.set("issueFrom", appliedFilters.issueFrom);
      if (appliedFilters.issueTo) params.set("issueTo", appliedFilters.issueTo);
      if (appliedFilters.status) params.set("status", appliedFilters.status);
      if (appliedFilters.hasBalanceDue) params.set("hasBalanceDue", "1");
      if (appliedFilters.rentImpairing) params.set("rentImpairing", "1");

      const url = `${BASE_URL}/api/violations/export?${params.toString()}`;
      const link = document.createElement("a");
      link.href = url;
      link.download = `violations-export-${Date.now()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("export error", err);
    }
  };

  const handleDelete = useCallback(
    async (bbl) => {
      if (!bbl) return;
      const ok = window.confirm(`Mark BBL ${bbl} as deleted?`);
      if (!ok) return;
      try {
        await axios.put(`${BASE_URL}/api/violations/bbl/${encodeURIComponent(bbl)}/isDeleted`, {
          isDeleted: 1,
        });
        await fetchData(page, pageSize, appliedFilters);
      } catch (err) {
        console.error("delete failed", err);
      }
    },
    [appliedFilters, fetchData, page, pageSize]
  );

  const handleRestore = useCallback(
    async (bbl) => {
      if (!bbl) return;
      const ok = window.confirm(`Restore BBL ${bbl}?`);
      if (!ok) return;
      try {
        await axios.put(`${BASE_URL}/api/violations/bbl/${encodeURIComponent(bbl)}/isDeleted`, {
          isDeleted: 0,
        });
        await fetchData(page, pageSize, appliedFilters);
      } catch (err) {
        console.error("restore failed", err);
      }
    },
    [appliedFilters, fetchData, page, pageSize]
  );

  const handleBulkDelete = useCallback(async () => {
    if (selectedBbls.size === 0) return;
    const list = Array.from(selectedBbls);
    const ok = window.confirm(`Mark ${list.length} BBL(s) as deleted?`);
    if (!ok) return;
    try {
      await axios.post(`${BASE_URL}/api/violations/bbl/bulk-delete`, { bbls: list });
      await fetchData(page, pageSize, appliedFilters);
    } catch (err) {
      console.error("bulk delete failed", err);
    }
  }, [selectedBbls, fetchData, page, pageSize, appliedFilters]);

  const handleBulkRestore = useCallback(async () => {
    if (selectedBbls.size === 0) return;
    const list = Array.from(selectedBbls);
    const ok = window.confirm(`Restore ${list.length} BBL(s)?`);
    if (!ok) return;
    try {
      await axios.post(`${BASE_URL}/api/violations/bbl/bulk-restore`, { bbls: list });
      await fetchData(page, pageSize, appliedFilters);
    } catch (err) {
      console.error("bulk restore failed", err);
    }
  }, [selectedBbls, fetchData, page, pageSize, appliedFilters]);

  const TooltipCell = ({ value, label = "Full", limit = 18 }) => {
    const full = value == null ? "" : String(value).trim();
    const isLong = full.length > limit;
    const short = isLong ? `${full.slice(0, limit)}...` : full;
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);
    const [pos, setPos] = useState({ top: 0, left: 0, width: 320 });

    useEffect(() => {
      if (!open) return;

      const updatePos = () => {
        const el = anchorRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        setPos({
          top: r.bottom + 8,
          left: Math.min(r.left, window.innerWidth - 460),
          width: Math.min(420, Math.max(260, r.width)),
        });
      };

      updatePos();

      const onDocMouseDown = (e) => {
        const el = anchorRef.current;
        if (!el) return;
        if (!el.contains(e.target)) setOpen(false);
      };

      const onKeyDown = (e) => {
        if (e.key === "Escape") setOpen(false);
      };

      window.addEventListener("scroll", updatePos, true);
      window.addEventListener("resize", updatePos);
      document.addEventListener("mousedown", onDocMouseDown);
      document.addEventListener("keydown", onKeyDown);

      return () => {
        window.removeEventListener("scroll", updatePos, true);
        window.removeEventListener("resize", updatePos);
        document.removeEventListener("mousedown", onDocMouseDown);
        document.removeEventListener("keydown", onKeyDown);
      };
    }, [open]);

    if (!full) return <span className="text-slate-400">-</span>;

    return (
      <div ref={anchorRef} className="w-full">
        <div className="flex items-center gap-2 w-full">
          <span className="block flex-1 min-w-0 truncate text-sm" style={{ userSelect: "text" }}>
            {short}
          </span>

          {isLong && (
            <button
              type="button"
              className="shrink-0 text-[11px] text-blue-600 underline hover:text-blue-700"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setOpen((v) => !v);
              }}
            >
              {open ? "Hide" : "View more"}
            </button>
          )}
        </div>

        {open &&
          ReactDOM.createPortal(
            <div
              className="fixed z-[999999]"
              style={{ top: pos.top, left: pos.left }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="rounded-lg border border-slate-200 bg-white shadow-xl p-2 text-xs text-slate-800 whitespace-normal break-words w-[420px] max-w-[calc(100vw-24px)]">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="font-semibold text-slate-700">{label}</div>
                  <button
                    className="text-[11px] text-slate-500 hover:text-slate-800"
                    onClick={() => setOpen(false)}
                    type="button"
                  >
                    x
                  </button>
                </div>
                <div style={{ userSelect: "text" }}>{full}</div>
              </div>
            </div>,
            document.body
          )}
      </div>
    );
  };

  function StatusPill({ value }) {
    const raw = (value ?? "").toString().trim();
    const v = raw ? raw : "NA";
    const key = v.toLowerCase();

    const base = "status-pill";
    const cls =
      key === "approved"
        ? "status-pill--approved"
        : key === "processing" || key === "in progress"
        ? "status-pill--processing"
        : key === "pending"
        ? "status-pill--pending"
        : key === "rejected"
        ? "status-pill--rejected"
        : "status-pill--na";

    return <span className={`${base} ${cls}`}>{v}</span>;
  }

  const stripHtml = (html) => {
    if (!html) return "";
    return String(html).replace(/<[^>]*>/g, "").replace(/\u00A0/g, " ").trim();
  };

  const NoteItem = ({ note }) => {
    const text = note?.text || note?.note || "";
    const isHtml = /<\/?[a-z][\s\S]*>/i.test(text || "");
    return (
      <div className="text-xs mb-2 pl-2 border-l-4 border-gray-300">
        <div
          className="text-gray-800 mb-1"
          dangerouslySetInnerHTML={{ __html: isHtml ? text : String(text || "").replace(/\n/g, "<br/>") }}
        />
        <div className="text-[11px] text-gray-500">{note?.timestamp || ""}</div>
      </div>
    );
  };

  const DetailCellRenderer = (props) => {
    const row = props.data || {};
    const notes = Array.isArray(row.notes) ? row.notes : [];
    const latestStatus = row.latest_status || "NA";
    const latestSubStatus = row.latest_sub_status || "NA";
    const latestLabel = row.latest_label || "NA";
    const latestFollowUp = row.latest_followUp_date || null;

    return (
      <div className="p-2 text-xs bg-[var(--surface-muted)]" style={{ whiteSpace: "pre-wrap", overflow: "visible" }}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
          <div className="flex items-center gap-2">
            <strong>Status:</strong> <StatusPill value={latestStatus} />
          </div>
          <div>
            <strong>Sub-Status:</strong> {latestSubStatus || "NA"}
          </div>
          <div>
            <strong>Label:</strong> {latestLabel || "NA"}
          </div>
          <div>
            <strong>Follow Up:</strong> {latestFollowUp ? formatDateMDY(latestFollowUp) || "�" : "�"}
          </div>
        </div>

        <div>
          <strong>Notes:</strong>
        </div>
        <div className="mt-1">
          {notes.length === 0 && <div className="text-xs text-gray-500">No notes</div>}
          {notes.map((n) => (
            <NoteItem key={n.id || n.timestamp || Math.random()} note={n} />
          ))}
        </div>
      </div>
    );
  };

  const baseColumnDefs = useMemo(
    () => [
      {
        headerName: "BBL",
        field: "bbl",
        colId: "bbl",
        width: 150,
        filter: "agTextColumnFilter",
        cellRenderer: (p) => {
          const bbl = p?.value;
          const href = bbl ? `/violations/bbl/${encodeURIComponent(bbl)}` : "#";
          return (
            <a
              href={href}
              target={bbl ? "_blank" : undefined}
              rel={bbl ? "noreferrer" : undefined}
              onClick={(e) => {
                e.stopPropagation();
                if (!bbl) e.preventDefault();
              }}
              className="text-blue-600 underline font-semibold"
              title="Open BBL detail in new tab"
            >
              {bbl || "-"}
            </a>
          );
        },
      },
      {
        headerName: "Violations",
        field: "violations_count",
        colId: "violations_count",
        width: 120,
        filter: "agNumberColumnFilter",
      },
      { headerName: "Source", field: "source", colId: "source", width: 90, filter: "agTextColumnFilter" },
      {
        headerName: "Owner",
        field: "owner_name",
        colId: "owner_name",
        minWidth: 180,
        flex: 1,
        filter: "agTextColumnFilter",
        cellRenderer: (p) => <TooltipCell value={p.value} label="Full Owner" limit={14} />,
      },
      {
        headerName: "Status",
        field: "latest_status",
        colId: "latest_status",
        width: 130,
        filter: "agTextColumnFilter",
        cellRenderer: (p) => <StatusPill value={p.value} />,
      },
      {
        headerName: "Notes",
        field: "notes_count",
        colId: "notes_count",
        width: 100,
        filter: "agNumberColumnFilter",
        cellRenderer: (params) => {
          const count = params?.data?.notes_count || 0;
          const isExpanded = !!params.node?.expanded;
          return (
            <div className="w-full flex items-center justify-center">
              <div className="flex items-center gap-2">
                <span className="text-xs">{count > 0 ? `${count}` : "0"}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    try {
                      const node = params.node;
                      const willOpen = !node.expanded;
                      params.api.forEachNode((n) => {
                        if (n && n.expanded) n.setExpanded(false);
                      });
                      node.setExpanded(willOpen);
                      params.api.refreshCells({ force: true });
                      params.api.resetRowHeights();
                    } catch (err) {
                      console.warn("toggle expand failed", err);
                    }
                  }}
                  title={isExpanded ? "Collapse" : "Expand"}
                  className={`w-5 h-5 rounded-full flex items-center justify-center shadow-md border ${
                    isExpanded ? "bg-red-500 text-white" : "bg-blue-600 text-white"
                  }`}
                >
                  {isExpanded ? <FaMinus size={10} /> : <FaPlus size={10} />}
                </button>
              </div>
            </div>
          );
        },
      },
      {
        headerName: "Address",
        colId: "address",
        valueGetter: (p) => {
          const r = p.data || {};
          const parts = [r.house_number, r.street, r.city, r.zip]
            .filter((v) => v != null && String(v).trim() !== "");
          if (parts.length) return parts.join(" ");
          return (
            r.address ||
            r.Address ||
            r.owner_address ||
            ""
          );
        },
        minWidth: 220,
        flex: 1,
        sortable: false,
        filter: "agTextColumnFilter",
        cellRenderer: (p) => <TooltipCell value={p.value} label="Full Address" limit={18} />,
      },
      {
        headerName: "Boro",
        field: "boro",
        colId: "boro",
        width: 90,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Block",
        field: "block",
        colId: "block",
        width: 110,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Lot",
        field: "lot",
        colId: "lot",
        width: 90,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Latest Issue Date",
        field: "latest_issue_date",
        colId: "latest_issue_date",
        valueFormatter: (p) => formatDateMDY(p.value),
        width: 140,
        filter: "agDateColumnFilter",
      },
      {
        headerName: "Latest Updated",
        field: "latest_updated_at",
        colId: "latest_updated_at",
        valueFormatter: (p) => formatDateMDY(p.value),
        width: 140,
        filter: "agDateColumnFilter",
      },
      {
        headerName: "Actions",
        colId: "actions",
        width: 110,
        sortable: false,
        filter: false,
        hide: !(tab === "all" || tab === "deleted"),
        cellRenderer: (p) => {
          const bbl = p?.data?.bbl;
          if (tab === "deleted") {
            return (
              <div className="w-full flex items-center justify-center">
                <button
                  type="button"
                  className="px-2 py-1 text-xs rounded border border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRestore(bbl);
                  }}
                >
                  Restore
                </button>
              </div>
            );
          }
          return (
            <div className="w-full flex items-center justify-center">
              <button
                type="button"
                className="px-2 py-1 text-xs rounded border border-red-200 text-red-600 hover:bg-red-50"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(bbl);
                }}
              >
                Delete
              </button>
            </div>
          );
        },
      },
    ],
    [handleDelete, handleRestore, tab]
  );

  const [columnDefs, setColumnDefs] = useState(baseColumnDefs);
  const [gridKey, setGridKey] = useState(0);

  const getColId = useCallback((def) => def.colId || def.field || def.headerName, []);

  const syncColumnsFromGrid = useCallback(() => {
    const api = columnApiRef.current;
    if (!api) return;
    try {
      const cols = api.getAllGridColumns?.() || api.getAllColumns?.() || [];
      const mapped = cols.map((col) => ({
        colId: col.getColId(),
        headerName: col.getColDef()?.headerName || col.getColId(),
        visible: col.isVisible(),
      }));
      if (mapped.length > 0) setAvailableColumns(mapped);
    } catch (e) {}
  }, []);

  const buildDynamicColumns = useCallback(
    (rows) => {
      if (!rows || rows.length === 0) return baseColumnDefs;
      const first = rows[0] || {};
      const keys = Object.keys(first);
      const baseFields = new Set(baseColumnDefs.map((c) => c.field).filter(Boolean));

      const preferredOrder = [
        "bbl",
        "violations_count",
        "boro",
        "block",
        "lot",
        "source",
        "owner_name",
        "latest_status",
        "notes_count",
        "house_number",
        "street",
        "city",
        "zip",
        "property_city",
        "description",
        "latest_issue_date",
        "latest_updated_at",
        "created_at",
        "updated_at",
      ];

      const toTitle = (key) =>
        String(key || "")
          .replace(/_/g, " ")
          .replace(/\b\w/g, (m) => m.toUpperCase());

      const extra = keys
        .filter((k) => !baseFields.has(k))
        .filter((k) => !/^placeholder\d+$/i.test(k))
        .sort((a, b) => {
          const ia = preferredOrder.indexOf(a);
          const ib = preferredOrder.indexOf(b);
          if (ia === -1 && ib === -1) return a.localeCompare(b);
          if (ia === -1) return 1;
          if (ib === -1) return -1;
          return ia - ib;
        })
        .map((k) => {
          const lower = k.toLowerCase();
          const isDate = lower.endsWith("_date") || lower.endsWith("_at");
          const isMoney =
            lower.includes("amount") || lower.includes("balance") || lower.includes("penalty");
          const isNumber =
            isMoney || lower.includes("id") || lower.includes("no") || lower.includes("number");

          return {
            headerName: toTitle(k),
            field: k,
            colId: k,
            minWidth: 140,
            hide: true,
            filter: isDate
              ? "agDateColumnFilter"
              : isNumber
                ? "agNumberColumnFilter"
                : "agTextColumnFilter",
            valueFormatter: isDate ? (p) => formatDateMDY(p.value) : undefined,
            cellRenderer: !isDate
              ? (p) => <TooltipCell value={p.value} label={toTitle(k)} limit={16} />
              : undefined,
          };
        });

      return [...baseColumnDefs, ...extra];
    },
    [baseColumnDefs]
  );

  useEffect(() => {
    if (columnApiRef.current) {
      syncColumnsFromGrid();
      return;
    }
    const fallback = columnDefs.map((c) => ({
      colId: c.colId || c.field || c.headerName,
      headerName: c.headerName || c.field || "Column",
      visible: !c.hide,
    }));
    if (fallback.length > 0) setAvailableColumns(fallback);
  }, [columnDefs, syncColumnsFromGrid]);

  const onSortChanged = useCallback(() => {
    if (tab === "hot") return;
    const model = gridApiRef.current?.getSortModel?.() || [];
    if (!model.length) {
      setSortBy("updated_at");
      setSortDir("DESC");
      return;
    }
    const first = model[0];
    setSortBy(first.colId);
    setSortDir(first.sort === "asc" ? "ASC" : "DESC");
  }, [tab]);

  const onRowClicked = () => {};

  const totalPages = Math.max(1, Math.ceil(total / pageSize || 1));
  const startRow = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endRow = Math.min(total, page * pageSize);

  const goFirst = () => page > 1 && setPage(1);
  const goPrev = () => page > 1 && setPage(page - 1);
  const goNext = () => page < totalPages && setPage(page + 1);
  const goLast = () => page < totalPages && setPage(totalPages);

  const goPage = (p) => {
    const np = Math.max(1, Math.min(totalPages, p));
    setPage(np);
  };

  const handleJumpSubmit = (e) => {
    e.preventDefault();
    const n = Number(pageInput);
    if (!n || n < 1) return;
    goPage(n);
    setPageInput("");
  };

  function renderPageButtons() {
    const maxButtons = 3;
    const half = Math.floor(maxButtons / 2);
    let start = Math.max(1, page - half);
    let end = Math.min(totalPages, start + maxButtons - 1);
    if (end - start + 1 < maxButtons) start = Math.max(1, end - maxButtons + 1);
    const buttons = [];
    for (let i = start; i <= end; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => goPage(i)}
          className={`px-3 py-1 border rounded text-sm ${i === page ? "bg-blue-600 text-white" : "bg-white"}`}
          aria-current={i === page ? "page" : undefined}
        >
          {i}
        </button>
      );
    }
    return buttons;
  }

  return (
    <div className="p-4 flex flex-col min-h-[80vh]">
      <div className="filter-panel mb-4">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="min-w-0">
            <div className="page-kicker">Violations</div>
            <h2 className="page-title truncate">Violation Home</h2>
            <div className="page-subtitle">NYC violation records</div>
          </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            {[
              { id: "all", label: "All" },
                { id: "hot", label: "Hot" },
                { id: "deleted", label: "Deleted" },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`px-2.5 py-0.5 rounded-full text-xs border-2 min-w-[86px] shadow-sm ${
                    tab === t.id
                      ? "bg-emerald-600 text-white border-emerald-600 dark:bg-emerald-500 dark:border-emerald-500"
                      : "bg-white hover:bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-900/70 dark:text-slate-200 dark:border-slate-600 dark:hover:bg-slate-800"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {tab === "all" && anySelected && (
              <button
                onClick={handleBulkDelete}
                className="ui-btn"
                title="Delete selected"
                type="button"
              >
                Delete Selected ({selectedBbls.size})
              </button>
            )}

            {tab === "deleted" && anySelected && (
              <button
                onClick={handleBulkRestore}
                className="ui-btn"
                title="Restore selected"
                type="button"
              >
                Restore Selected ({selectedBbls.size})
              </button>
            )}

            <button
              onClick={() => setShowColumns((v) => !v)}
              className="ui-btn"
              title="Show/hide columns"
            >
              Columns
            </button>
            <button
              onClick={() => setShowAdvanced((v) => !v)}
              className="ui-btn"
              title="Toggle advanced filters"
            >
              {showAdvanced ? "Hide Filters" : "More Filters"}
            </button>
            <button
              onClick={onExportCsv}
              className="ui-btn"
              title="Export current filters as CSV"
            >
              Export CSV
            </button>

            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              className="ui-select"
              title="Rows per page"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        <div className="filter-grid mt-3 grid-cols-1 md:grid-cols-2 lg:grid-cols-6">
          <div>
            <label className="text-[11px] font-semibold text-[var(--fg-muted)]">Global search</label>
            <input
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              placeholder="Owner, violation #, address, BBL"
              className="ui-input w-full"
            />
          </div>

          <div>
            <label className="text-[11px] font-semibold text-[var(--fg-muted)]">Source</label>
            <select
              value={filters.source}
              onChange={(e) => setFilters({ ...filters, source: e.target.value })}
              className="ui-select w-full"
            >
              <option value="">All</option>
              {(meta.sources && meta.sources.length > 0 ? meta.sources : ["DOB", "ECB", "HMC"]).map((src) => (
                <option key={src} value={src}>
                  {src}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[11px] font-semibold text-[var(--fg-muted)]">Boro</label>
            <select
              value={filters.boro}
              onChange={(e) => setFilters({ ...filters, boro: e.target.value })}
              className="ui-select w-full"
            >
              {BORO_OPTIONS.map((o) => (
                <option key={o.label || o.value} value={o.value}>
                  {o.label || "All"}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[11px] font-semibold text-[var(--fg-muted)]">Status</label>
            <input
              list="violation-status-options"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              placeholder="Current / Hearing / ECB / HMC"
              className="ui-input w-full"
            />
            <datalist id="violation-status-options">
              {(meta.statuses || []).map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          </div>

          <div>
            <label className="text-[11px] font-semibold text-[var(--fg-muted)]">Issue date from</label>
            <input
              type="date"
              value={filters.issueFrom}
              onChange={(e) => setFilters({ ...filters, issueFrom: e.target.value })}
              className="ui-input w-full"
            />
          </div>

          <div>
            <label className="text-[11px] font-semibold text-[var(--fg-muted)]">Issue date to</label>
            <input
              type="date"
              value={filters.issueTo}
              onChange={(e) => setFilters({ ...filters, issueTo: e.target.value })}
              className="ui-input w-full"
            />
          </div>

          {showAdvanced && (
            <>
              <div>
                <label className="text-[11px] font-semibold text-[var(--fg-muted)]">Block</label>
                <input
                  value={filters.block}
                  onChange={(e) => setFilters({ ...filters, block: e.target.value })}
                  placeholder="Block"
                  className="ui-input w-full"
                />
              </div>

              <div>
                <label className="text-[11px] font-semibold text-[var(--fg-muted)]">Lot</label>
                <input
                  value={filters.lot}
                  onChange={(e) => setFilters({ ...filters, lot: e.target.value })}
                  placeholder="Lot"
                  className="ui-input w-full"
                />
              </div>

              <div className="flex items-center gap-4 h-7 mt-4">
                <label className="inline-flex items-center gap-2 text-[11px] text-[var(--fg-muted)]">
                  <input
                    type="checkbox"
                    checked={filters.hasBalanceDue}
                    onChange={(e) => setFilters({ ...filters, hasBalanceDue: e.target.checked })}
                  />
                  Has balance due
                </label>

                <label className="inline-flex items-center gap-2 text-[11px] text-[var(--fg-muted)]">
                  <input
                    type="checkbox"
                    checked={filters.rentImpairing}
                    onChange={(e) => setFilters({ ...filters, rentImpairing: e.target.checked })}
                  />
                  Rent impairing
                </label>
              </div>
            </>
          )}
        </div>

        <div className="filter-actions">
          <button
            onClick={applyFilters}
            className="ui-btn ui-btn-primary"
            disabled={loading}
          >
            Search
          </button>
          <button
            onClick={clearFilters}
            className="ui-btn"
            disabled={loading}
          >
            Clear
          </button>
          {appliedChips.length > 0 && (
            <div className="text-[11px] text-slate-500 dark:text-slate-400 ml-2">
              {appliedChips.length} filter(s) applied
            </div>
          )}
        </div>

        {appliedChips.length > 0 && (
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {appliedChips.map((chip) => (
              <button
                key={chip.key}
                onClick={() => removeChip(chip.key)}
                className="text-[11px] px-2 py-0.5 rounded-full border bg-white hover:bg-slate-50 dark:bg-slate-900/70 dark:text-slate-200 dark:border-slate-600 dark:hover:bg-slate-800"
                title="Remove filter"
              >
                {chip.label} x
              </button>
            ))}
          </div>
        )}
      </div>

      {showColumns && availableColumns.length > 0 && (
        <div className="mb-2 p-2 bg-white border rounded shadow-sm">
          <div className="text-xs font-semibold text-slate-600 mb-2">Columns</div>
          <div className="flex flex-wrap gap-3 max-h-40 overflow-auto pr-2">
            {availableColumns.map((c) => (
              <label key={c.colId} className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={c.visible}
                  onChange={() => {
                    const next = !c.visible;
                    setAvailableColumns((prev) =>
                      prev.map((p) => (p.colId === c.colId ? { ...p, visible: next } : p))
                    );
                    setColumnDefs((prev) =>
                      prev.map((d) => {
                        const id = getColId(d);
                        if (id === c.colId) return { ...d, hide: !next };
                        return d;
                      })
                    );
                    setGridKey((k) => k + 1);
                  }}
                />
                {c.headerName}
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-lg border flex-1 bg-white overflow-hidden">
        <AppDataGrid
          className=""
          rowData={rowData}
          columnDefs={columnDefs}
          height={gridHeight}
          minWidth={1000}
          gridRef={gridRef}
          rowSelection={{
            mode: "multiRow",
            enableClickSelection: false,
            checkboxes: tab === "all" || tab === "deleted",
            headerCheckbox: tab === "all" || tab === "deleted",
            selectionColumnDef: {
              width: 46,
              minWidth: 46,
              maxWidth: 46,
              suppressMenu: true,
              sortable: false,
              resizable: false,
              filter: false,
            },
          }}
          defaultColDef={{
            sortable: true,
            resizable: true,
            filter: false,
            floatingFilter: false,
            filterParams: { buttons: ["reset"], closeOnApply: true },
            minWidth: 80,
            cellClass: "text-sm",
            headerClass: "text-center",
            cellStyle: { userSelect: "text" },
          }}
          floatingFiltersHeight={0}
          enableCellTextSelection={true}
          ensureDomOrder={true}
          rowHeight={42}
          headerHeight={36}
          onGridReady={(params) => {
            gridApiRef.current = params.api;
            columnApiRef.current = params.columnApi;
            setTimeout(() => params.api.sizeColumnsToFit?.(), 50);
            syncColumnsFromGrid();
          }}
          onSortChanged={onSortChanged}
          onRowClicked={onRowClicked}
          onSelectionChanged={() => {
            const nodes = gridApiRef.current?.getSelectedNodes?.() || [];
            const next = new Set();
            nodes.forEach((n) => {
              const bbl = n?.data?.bbl;
              if (bbl) next.add(bbl);
            });
            setSelectedBbls(next);
          }}
          overlayLoadingTemplate='<span class="ag-overlay-loading-center">Loading data...</span>'
          overlayNoRowsTemplate='<span class="ag-overlay-loading-center">No records to display</span>'
          masterDetail={true}
          detailCellRenderer={DetailCellRenderer}
          detailRowHeight={220}
          isRowMaster={() => true}
          pagination={false}
          domLayout="normal"
          getRowId={(params) => (params.data && params.data.bbl ? params.data.bbl : `${Math.random()}`)}
          loading={loading}
        />
      </div>

      <div className="flex flex-col md:flex-row items-center justify-between gap-3 mt-4">
        <div className="text-sm text-slate-600">
          {total === 0 ? (
            <>No records</>
          ) : (
            <>
              Showing <strong>{startRow}</strong> - <strong>{endRow}</strong> of <strong>{total}</strong>
            </>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={goFirst} disabled={page <= 1} className="px-3 py-1 border rounded disabled:opacity-60 text-sm">
            First
          </button>
          <button onClick={goPrev} disabled={page <= 1} className="px-3 py-1 border rounded disabled:opacity-60 text-sm">
            Prev
          </button>

          <div className="flex items-center gap-1">{renderPageButtons()}</div>

          <button
            onClick={goNext}
            disabled={page >= totalPages}
            className="px-3 py-1 border rounded disabled:opacity-60 text-sm"
          >
            Next
          </button>
          <button
            onClick={goLast}
            disabled={page >= totalPages}
            className="px-3 py-1 border rounded disabled:opacity-60 text-sm"
          >
            Last
          </button>

          <div className="ml-2 text-sm text-slate-600">
            Page <strong>{page}</strong> of <strong>{totalPages}</strong>
          </div>

          <form onSubmit={handleJumpSubmit} className="flex items-center gap-1 ml-2">
            <input
              type="number"
              min="1"
              max={totalPages}
              value={pageInput}
              onChange={(e) => setPageInput(e.target.value)}
              placeholder="Go to"
              className="ui-input w-20"
            />
            <button type="submit" className="px-3 py-1 border rounded text-sm">
              Go
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
